import axiosInstance from '@/app/api/axiosInstance';
import { format, startOfMonth, startOfDay, isAfter, parseISO } from 'date-fns';
import { PaginatedResponse } from '@/app/types/api';
import { API_BASE_URL } from '@/app/config/api';
import { JobCardStatus, JobCardPriority, JobCardListItem } from '@/app/types/jobCard';

export interface DailySummary {
  summary_date: string;
  total_sales: number;
  total_payments_received: number;
  sales_invoice_count: number;
  payment_received_count: number;
}

export interface DashboardStats {
  activeCustomers: number;
  totalItems: number;
  lowStockAlerts: number;
  pendingOrders: number;
  pendingOrdersValue: number;
  monthToDateSales: number;
  monthToDateTarget: number;
}

export interface DashboardDateRangeStats {
  totalSales: number;
  salesCount: number;
  totalPayments: number;
  paymentsCount: number;
  startDate: string;
  endDate: string;
}

export interface RecentActivity {
  id: string;
  type: 'sale' | 'payment' | 'order' | 'expense';
  title: string;
  description: string;
  amount?: number;
  time: string;
  user?: {
    name: string;
    avatar?: string;
  };
}

export const dashboardService = {
  async getDailySummary(date?: Date): Promise<DailySummary> {
    const params = date ? { summary_date: format(date, 'yyyy-MM-dd') } : {};
    const response = await axiosInstance.get<DailySummary>('/reports/daily-summary', { params });
    return response.data;
  },

  async getDashboardDateRangeStats(startDate: Date, endDate: Date): Promise<DashboardDateRangeStats> {
    try {
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      
      // If it's a single day, use the daily summary endpoint which we know works
      if (start === end) {
        const dailySummary = await this.getDailySummary(startDate);
        return {
          totalSales: dailySummary.total_sales,
          salesCount: dailySummary.sales_invoice_count,
          totalPayments: dailySummary.total_payments_received,
          paymentsCount: dailySummary.payment_received_count,
          startDate: start,
          endDate: end
        };
      }
      
      // For date ranges, aggregate daily summaries
      const currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      const dailySummaries: DailySummary[] = [];
      
      while (currentDate <= endDateObj) {
        try {
          const summary = await this.getDailySummary(new Date(currentDate));
          dailySummaries.push(summary);
        } catch (error) {
          console.log(`No data for ${format(currentDate, 'yyyy-MM-dd')}`);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Aggregate the results
      const totalSales = dailySummaries.reduce((sum, day) => sum + day.total_sales, 0);
      const salesCount = dailySummaries.reduce((sum, day) => sum + day.sales_invoice_count, 0);
      const totalPayments = dailySummaries.reduce((sum, day) => sum + day.total_payments_received, 0);
      const paymentsCount = dailySummaries.reduce((sum, day) => sum + day.payment_received_count, 0);

      return {
        totalSales,
        salesCount,
        totalPayments,
        paymentsCount,
        startDate: start,
        endDate: end
      };
    } catch (error: any) {
      console.error('Error fetching dashboard date range stats:', error);
      return {
        totalSales: 0,
        salesCount: 0,
        totalPayments: 0,
        paymentsCount: 0,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };
    }
  },

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Get party count first
      let partyCount = 0;
      try {
        const partyResponse = await fetch(`${API_BASE_URL}/parties/?limit=1`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        const partyData = await partyResponse.json();
        partyCount = partyData.total || 0;
      } catch (e) {
        partyCount = 0;
      }

      // Fetch all data in parallel
      const [_, items, lowStock, pendingOrders, monthSales] = await Promise.all([
        Promise.resolve(null), // placeholder for parties since we already fetched it
        
        // Get total items
        axiosInstance.get<PaginatedResponse<any>>('/items/', {
          params: { limit: 1 }
        }),
        
        // Get low stock alerts
        axiosInstance.get<PaginatedResponse<any>>('/reports/low-stock-summary', {
          params: { limit: 1 }
        }),
        
        // Get pending sales orders
        axiosInstance.get<PaginatedResponse<any>>('/documents/', {
          params: { 
            limit: 100,
            filters: JSON.stringify([
              { field: 'document_type', value: 'Sales Order', op: 'eq' },
              { field: 'status', value: 'Pending', op: 'eq' }
            ])
          }
        }),
        
        // Get month to date sales
        this.getMonthToDateSales()
      ]);

      // Calculate pending orders value
      let pendingOrdersValue = 0;
      if (pendingOrders.data.items && pendingOrders.data.items.length > 0) {
        pendingOrdersValue = pendingOrders.data.items.length * 100000; // Rough estimate
      }

      const stats = {
        activeCustomers: partyCount,
        totalItems: items?.data?.total || 0,
        lowStockAlerts: lowStock?.data?.total || 0,
        pendingOrders: pendingOrders?.data?.total || 0,
        pendingOrdersValue,
        monthToDateSales: monthSales,
        monthToDateTarget: 1500000 // Default target, can be made configurable
      };

      console.warn('✅ Dashboard stats calculated:', stats);
      console.warn('🔢 Party count (activeCustomers):', stats.activeCustomers);
      return stats;
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      // Return default values on error
      return {
        activeCustomers: 0,
        totalItems: 0,
        lowStockAlerts: 0,
        pendingOrders: 0,
        pendingOrdersValue: 0,
        monthToDateSales: 0,
        monthToDateTarget: 1500000
      };
    }
  },

  async getMonthToDateSales(): Promise<number> {
    try {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const dateRangeStats = await this.getDashboardDateRangeStats(monthStart, now);
      return dateRangeStats.totalSales;
    } catch (error) {
      console.error('Error fetching month to date sales:', error);
      return 0;
    }
  },

  async getRecentActivities(): Promise<RecentActivity[]> {
    try {
      // Fetch all recent vouchers sorted by creation date
      const response = await axiosInstance.get<PaginatedResponse<any>>('/vouchers/', {
        params: {
          limit: 20,
          sort_by: 'created_at',
          sort_dir: 'desc'
        }
      });

      const activities: RecentActivity[] = [];

      // Process vouchers
      if (response.data.items) {
        response.data.items.forEach((voucher: any) => {
          // Map voucher types to activity types
          let activityType: 'sale' | 'payment' | 'order' | 'expense' = 'sale';
          let title = '';
          
          switch (voucher.voucher_type_id) {
            case 1: // Sales Invoice
              activityType = 'sale';
              title = 'New Sale Invoice';
              break;
            case 3: // Payment Made
            case 4: // Payment Received
              activityType = 'payment';
              title = voucher.voucher_type_id === 4 ? 'Payment Received' : 'Payment Made';
              break;
            case 2: // Purchase Invoice
              activityType = 'expense';
              title = 'Purchase Invoice';
              break;
            default:
              title = voucher.voucher_type?.name || 'Transaction';
          }

          activities.push({
            id: voucher.id.toString(),
            type: activityType,
            title,
            description: `${voucher.voucher_number} - ${voucher.party?.name || 'Unknown'}`,
            amount: parseFloat(voucher.total_amount),
            time: this.getRelativeTime(new Date(voucher.created_at)),
            user: {
              name: voucher.created_by?.name || 'System'
            }
          });
        });
      }

      return activities.slice(0, 10); // Return top 10 activities
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  },

  getRelativeTime(date: Date): string {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const mins = Math.floor(diffInSeconds / 60);
      return `${mins} min${mins > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  }
};

// Workshop Dashboard Types
export interface JobCardStatusCount {
  status: JobCardStatus;
  count: number;
}

export interface TodayStats {
  newArrivals: number;
  inProgress: number;
  readyForDelivery: number;
  deliveredToday: number;
}

export interface PendingActionItem {
  id: number;
  documentNumber: string;
  vehicle: {
    registrationNumber: string;
    make: string | null;
    model: string | null;
  };
  party: {
    name: string;
  } | null;
  transactionDate: string;
  daysWaiting: number;
  status: JobCardStatus;
}

export interface PriorityJobItem {
  id: number;
  documentNumber: string;
  vehicle: {
    registrationNumber: string;
    make: string | null;
    model: string | null;
  };
  priority: JobCardPriority;
  status: JobCardStatus;
  estimatedCompletionDate: string | null;
  daysOverdue: number;
  isOverdue: boolean;
}

// Workshop Dashboard Service
export const workshopDashboardService = {
  // Get job card counts by status
  async getJobCardStatusCounts(): Promise<JobCardStatusCount[]> {
    const activeStatuses: JobCardStatus[] = [
      'arrival',
      'inspection',
      'quoted',
      'approved',
      'under_service',
      'quality_check',
      'ready',
      'delivered'
    ];

    try {
      // Make parallel requests for each status
      const responses = await Promise.all(
        activeStatuses.map(status =>
          axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
            params: { status, limit: 1 } // Only need count, not items
          })
        )
      );

      return activeStatuses.map((status, index) => ({
        status,
        count: responses[index].data.total || 0
      }));
    } catch (error) {
      console.error('Error fetching job card status counts:', error);
      return activeStatuses.map(status => ({ status, count: 0 }));
    }
  },

  // Get today's stats
  async getTodayStats(): Promise<TodayStats> {
    const today = format(new Date(), 'yyyy-MM-dd');

    try {
      // Parallel requests for today's data
      const [arrivalsResponse, inProgressResponse, readyResponse, deliveredResponse] = await Promise.all([
        // New arrivals today (transaction_date = today, any status)
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: {
            transaction_date_from: today,
            transaction_date_to: today,
            limit: 1
          }
        }),
        // In progress (under_service status)
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { status: 'under_service', limit: 1 }
        }),
        // Ready for delivery
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { status: 'ready', limit: 1 }
        }),
        // Delivered today - fetch delivered status and filter by date client-side
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { status: 'delivered', limit: 100 }
        })
      ]);

      // Count delivered today by checking dates
      const deliveredToday = deliveredResponse.data.items?.filter(item => {
        const itemDate = item.transaction_date?.split('T')[0];
        return itemDate === today;
      }).length || 0;

      return {
        newArrivals: arrivalsResponse.data.total || 0,
        inProgress: inProgressResponse.data.total || 0,
        readyForDelivery: readyResponse.data.total || 0,
        deliveredToday
      };
    } catch (error) {
      console.error('Error fetching today stats:', error);
      return {
        newArrivals: 0,
        inProgress: 0,
        readyForDelivery: 0,
        deliveredToday: 0
      };
    }
  },

  // Get pending actions (quoted + quality_check)
  async getPendingActions(): Promise<{
    awaitingApproval: PendingActionItem[];
    qualityCheckNeeded: PendingActionItem[];
  }> {
    try {
      const [quotedResponse, qcResponse] = await Promise.all([
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { status: 'quoted', limit: 20, sort_by: 'transaction_date', sort_dir: 'asc' }
        }),
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { status: 'quality_check', limit: 20, sort_by: 'transaction_date', sort_dir: 'asc' }
        })
      ]);

      const now = new Date();

      const mapToActionItem = (item: JobCardListItem): PendingActionItem => {
        const transactionDate = parseISO(item.transaction_date);
        const daysWaiting = Math.floor((now.getTime() - transactionDate.getTime()) / (1000 * 60 * 60 * 24));

        return {
          id: item.id,
          documentNumber: item.document_number,
          vehicle: {
            registrationNumber: item.vehicle.registration_number,
            make: item.vehicle.make,
            model: item.vehicle.model
          },
          party: item.party ? { name: item.party.name } : null,
          transactionDate: item.transaction_date,
          daysWaiting: Math.max(0, daysWaiting),
          status: item.status
        };
      };

      return {
        awaitingApproval: (quotedResponse.data.items || []).map(mapToActionItem),
        qualityCheckNeeded: (qcResponse.data.items || []).map(mapToActionItem)
      };
    } catch (error) {
      console.error('Error fetching pending actions:', error);
      return { awaitingApproval: [], qualityCheckNeeded: [] };
    }
  },

  // Get priority and overdue jobs
  async getPriorityAndOverdueJobs(): Promise<PriorityJobItem[]> {
    try {
      // Fetch urgent and high priority jobs that are not in terminal states
      const [urgentResponse, highResponse, allActiveResponse] = await Promise.all([
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { priority: 'urgent', limit: 20 }
        }),
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { priority: 'high', limit: 20 }
        }),
        // Get all active jobs to check for overdue
        axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
          params: { limit: 100 }
        })
      ]);

      const now = new Date();
      const today = startOfDay(now);
      const terminalStatuses: JobCardStatus[] = ['delivered', 'invoiced', 'cancelled'];

      const mapToPriorityItem = (item: JobCardListItem): PriorityJobItem => {
        let daysOverdue = 0;
        let isOverdue = false;

        if (item.estimated_completion_date) {
          const estimatedDate = parseISO(item.estimated_completion_date);
          if (isAfter(today, estimatedDate)) {
            isOverdue = true;
            daysOverdue = Math.floor((today.getTime() - estimatedDate.getTime()) / (1000 * 60 * 60 * 24));
          }
        }

        return {
          id: item.id,
          documentNumber: item.document_number,
          vehicle: {
            registrationNumber: item.vehicle.registration_number,
            make: item.vehicle.make,
            model: item.vehicle.model
          },
          priority: item.priority,
          status: item.status,
          estimatedCompletionDate: item.estimated_completion_date,
          daysOverdue,
          isOverdue
        };
      };

      // Combine urgent and high priority, filter out terminal statuses
      const priorityJobs = [
        ...(urgentResponse.data.items || []),
        ...(highResponse.data.items || [])
      ]
        .filter(item => !terminalStatuses.includes(item.status))
        .map(mapToPriorityItem);

      // Find overdue jobs from all active
      const overdueJobs = (allActiveResponse.data.items || [])
        .filter(item => {
          if (terminalStatuses.includes(item.status)) return false;
          if (!item.estimated_completion_date) return false;
          const estimatedDate = parseISO(item.estimated_completion_date);
          return isAfter(today, estimatedDate);
        })
        .map(mapToPriorityItem);

      // Combine and deduplicate by id
      const combined = [...priorityJobs, ...overdueJobs];
      const unique = combined.filter((item, index, self) =>
        index === self.findIndex(t => t.id === item.id)
      );

      // Sort: overdue first, then by priority (urgent > high > normal > low)
      const priorityOrder: Record<JobCardPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
      unique.sort((a, b) => {
        if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

      return unique.slice(0, 10); // Limit to 10 items
    } catch (error) {
      console.error('Error fetching priority/overdue jobs:', error);
      return [];
    }
  },

  // Get recent job cards
  async getRecentJobCards(limit: number = 10): Promise<JobCardListItem[]> {
    try {
      const response = await axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
        params: {
          limit,
          sort_by: 'updated_at',
          sort_dir: 'desc'
        }
      });
      return response.data.items || [];
    } catch (error) {
      console.error('Error fetching recent job cards:', error);
      return [];
    }
  }
};